import type { IProjectInviteNotificationData } from "@frontend-shared/types";
import { UserPlus } from "lucide-react";
import { memo, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";

import { useTranslateRoleName } from "@/hooks/use-translate-role-name";

import { NotificationCardShell } from "../NotificationCardShell";

interface IProjectInviteCardProps {
  uuid: string;
  data: IProjectInviteNotificationData;
  createdAt: string;
  isRead: boolean;
  onViewProject: (organizationUuid: string) => void;
}

const ProjectInviteCardComponent = ({
  uuid,
  data,
  createdAt,
  isRead,
  onViewProject,
}: IProjectInviteCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const translateRoleName = useTranslateRoleName();

  const handleViewProject = useCallback(
    () => onViewProject(data.organizationUuid),
    [onViewProject, data.organizationUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId="project_invite"
      createdAt={createdAt}
      isRead={isRead}
      icon={<UserPlus className="h-5 w-5" />}
      ctaLabel={t("view_project")}
      onCtaClick={handleViewProject}
      body={
        <Trans
          i18nKey="type.project_invite.body"
          t={t}
          values={{
            inviterName: data.inviterName,
            projectTitle: data.projectTitle,
            roleName: translateRoleName(data.roleName),
          }}
          // NOTE: NSAI-6124 — these values are another user's input and `<Trans>`
          // parses its result as markup (`Bob<p>` became an element; a
          // `<b style="x">` merged attributes into the mapped component and
          // threw). Escaping them before the parse and unescaping the text
          // afterwards keeps them literal; only the copy's own tags render.
          tOptions={{ interpolation: { escapeValue: true } }}
          shouldUnescape
          components={{ b: <span className="font-medium" /> }}
        />
      }
    />
  );
};

export const ProjectInviteCard = memo(ProjectInviteCardComponent);
